import { useState } from "react";
import { FiCopy } from "react-icons/fi";
import { capitalizeFirstLetter } from "../utils/Functions";
import { formatPhoneNumber } from "../utils/Functions";
import moment from "moment";

const PartnerDetails = ({ user, page }) => {
  const [copied, setCopied] = useState(false);
  // Keep legacy partner records readable until their capacity is migrated.
  const bedCount = user?.basicInfo?.bedCount ?? user?.basicInfo?.patientCount;
  const serviceEntries = Array.isArray(user?.services)
    ? user.services
    : user?.services && typeof user.services === "object"
      ? Object.entries(user.services)
          .filter(
            ([, value]) =>
              value === true ||
              (value &&
                typeof value === "object" &&
                value.enabled !== false &&
                value.active !== false),
          )
          .map(([name, value]) =>
            value && typeof value === "object" ? { name, ...value } : { name },
          )
      : [];
  const fallbackCommission = Number(user?.payout?.commission);
  const enabledServices = serviceEntries.reduce((services, service) => {
    if (
      service &&
      typeof service === "object" &&
      (service.enabled === false || service.active === false)
    ) {
      return services;
    }

    const rawName =
      typeof service === "string"
        ? service
        : service?.name ||
          service?.value ||
          service?.label ||
          service?.serviceType ||
          service?.service ||
          service?.type;
    const normalizedName = String(rawName || "")
      .trim()
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .toLowerCase();
    if (!normalizedName || services.some((item) => item.key === normalizedName)) {
      return services;
    }

    const serviceCommission =
      typeof service === "object"
        ? service?.commission ?? service?.ceviliyarCommission
        : undefined;
    const parsedCommission =
      serviceCommission !== undefined &&
      serviceCommission !== null &&
      String(serviceCommission).trim() !== ""
        ? Number(serviceCommission)
        : Number.NaN;
    const commissionValue = Number.isFinite(parsedCommission)
      ? parsedCommission
      : fallbackCommission;

    return [
      ...services,
      {
        key: normalizedName,
        displayName: normalizedName.replace(/\b\w/g, (letter) => letter.toUpperCase()),
        commission: commissionValue,
      },
    ];
  }, []);

  const handleCopy = () => {
    if (user?.referralCode) {
      navigator.clipboard.writeText(user.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  console.log("partner details", user);

  return (
    <div className="p-6">
      {/* Row 1: General Information || Address Details */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* General Information */}
        <div>
          <h3 className="text-[14px] font-poppins font-semibold text-gray-800 mb-4">
            General Information
          </h3>
          <div className="bg-[#F6F8FC] shadow-sm rounded-[2px] p-4 space-y-3">
            <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
              <span className="text-gray-600 font-poppins">Name</span>
              <span className="text-[#787998] font-poppins">
                : {capitalizeFirstLetter(user?.basicInfo?.name)}
              </span>
            </div>
            {user?.providerType === "individual" ? (
              <>
                <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                  <span className="text-gray-600 font-poppins">DOB</span>
                  <span className="text-[#787998] font-poppins">
                    :{" "}
                    {moment(user?.basicInfo?.dob).format("DD-MM-YYYY") || "N/A"}
                  </span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                  <span className="text-gray-600 font-poppins">Email</span>
                  <span className="text-[#787998] font-poppins">
                    : {user?.basicInfo?.email || "N/A"}
                  </span>
                </div>
              </>
            ) : (
              <>
                {" "}
                <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                  <span className="text-gray-600 font-poppins">Slogan</span>
                  <span className="text-[#787998] font-poppins">
                    : {user?.basicInfo?.slogan || "N/A"}
                  </span>
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                  <span className="text-gray-600 font-poppins">
                    No of Beds
                  </span>
                  <span className="text-[#787998] font-poppins">
                    : {bedCount ?? "N/A"}
                  </span>
                </div>
              </>
            )}
            <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
              <span className="text-gray-600 font-poppins">Referral code</span>
              <span className="text-[#787998] font-medium font-poppins flex items-center gap-2">
                : {user?.referralCode || "N/A"}
                {user?.referralCode && (
                  <>
                    <button
                      onClick={handleCopy}
                      className="text-gray-500 hover:text-indigo-600 transition-colors"
                      title="Copy referral code"
                    >
                      <FiCopy size={12} />
                    </button>
                    {copied && (
                      <span className="text-[10px] text-green-600">
                        Copied!
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Address Details */}
        <div>
          <h3 className="text-[14px] font-poppins font-semibold text-gray-800 mb-4">
            Address Details
          </h3>
          <div className="bg-[#F6F8FC] shadow-sm rounded-[2px] p-4 space-y-3">
            <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
              <span className="text-gray-600 font-poppins">Address Line 1</span>
              <span className="text-[#787998] font-poppins">
                : {user?.addressInfo?.address1 || "N/A"}
              </span>
            </div>
            <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
              <span className="text-gray-600 font-poppins">Address Line 2</span>
              <span className="text-[#787998] font-poppins">
                : {user?.addressInfo?.address2 || "N/A"}
              </span>
            </div>
            <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
              <span className="text-gray-600 font-poppins">City</span>
              <span className="text-[#787998] font-poppins">
                : {capitalizeFirstLetter(user?.addressInfo?.city) || "N/A"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid grid-cols-[60px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">State</span>
                <span className="text-[#787998] font-poppins ml-[60px]">
                  : {capitalizeFirstLetter(user?.addressInfo?.state) || "N/A"}
                </span>
              </div>
              <div className="grid grid-cols-[60px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">Pin code</span>
                <span className="text-[#787998] font-poppins">
                  : {user?.addressInfo?.pincode || "N/A"}
                </span>
              </div>
              {page === "admin" && (
                <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px] col-span-2">
                  <span className="text-gray-600 font-poppins">Service Radius</span>
                  <span className="text-[#787998] font-poppins">
                    : {user?.addressInfo?.serviceRadius != null
                      ? `${user.addressInfo.serviceRadius} km`
                      : "N/A"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Contact Person Info || Contact Person Info 2 */}
      {user?.providerType !== "individual" && (
        <div className="grid grid-cols-2 gap-6 ">
          {/* Primary Contact */}
          <div>
            <h3 className="text-[14px] font-poppins font-semibold text-gray-800 mb-4">
              Contact Person Information
            </h3>
            <div className="bg-[#F6F8FC] shadow-sm rounded-[2px] p-4 space-y-3">
              <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">
                  Primary Contact
                </span>
                <span className="text-[#787998] font-poppins">
                  : {user?.contactInfo?.[0]?.name || "N/A"}
                </span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">Phone Number</span>
                <span className="text-[#787998] font-poppins">
                  : {formatPhoneNumber(user?.contactInfo?.[0]?.phone) || "N/A"}
                </span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">Email ID</span>
                <span className="text-[#787998] font-poppins">
                  : {user?.contactInfo?.[0]?.email || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Secondary Contact */}
          <div>
            <h3 className="text-[14px] font-poppins font-semibold text-gray-800 mb-4">
              Contact Person Information
            </h3>
            <div className="bg-[#F6F8FC] shadow-sm rounded-[2px] p-4 space-y-3">
              <div className="grid grid-cols-[130px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">
                  Secondary Contact
                </span>
                <span className="text-[#787998] font-poppins">
                  : {user?.contactInfo?.[1]?.name || "N/A"}
                </span>
              </div>
              <div className="grid grid-cols-[130px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">Phone Number</span>
                <span className="text-[#787998] font-poppins">
                  : {formatPhoneNumber(user?.contactInfo?.[1]?.phone) || "N/A"}
                </span>
              </div>
              <div className="grid grid-cols-[130px_1fr] gap-2 text-[12px]">
                <span className="text-gray-600 font-poppins">Email ID</span>
                <span className="text-[#787998] font-poppins">
                  : {user?.contactInfo?.[1]?.email || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {page === "admin" && user?.providerType !== "individual" && (
        <div className="mt-6">
          <h3 className="text-[14px] font-poppins font-semibold text-gray-800 mb-4">
            Services
          </h3>
          <div className="bg-[#F6F8FC] shadow-sm rounded-[2px] p-4">
            {enabledServices.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {enabledServices.map((service) => (
                  <div
                    className="flex items-center justify-between gap-3 rounded border border-[#E4E8F0] bg-white px-3 py-2 text-[12px]"
                    key={service.key}
                  >
                    <span className="text-gray-600 font-poppins">
                      {service.displayName}
                    </span>
                    <span className="text-[#787998] font-poppins whitespace-nowrap">
                      {Number.isFinite(service.commission)
                        ? `${service.commission}%`
                        : "N/A"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-[#787998] font-poppins text-[12px]">
                No services enabled
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDetails;
